/**
 * categoryId 형식 규칙.
 *
 * 형식: cat_{대역 1자리}{일련번호 5자리}  예) cat_300182
 *
 * **지금 발급되는 것은 `cat_3` 하나뿐이다.** 카테고리가 평탄해지기 전에는
 * 그룹용으로 `cat_1`(최상위)·`cat_2`(하위)를 썼다. 그 id들은 **은퇴**했다 —
 * 지워진 것이 아니라 재사용을 막기 위해 lock에 남아 있다(category-ids.lock.json).
 *
 * 대역을 남겨두는 이유: `PICK#cat_100001`처럼 은퇴한 그룹을 가리키는 옛 참조를
 * **DB에 닿기 전에 문자열만 보고** 걸러낼 수 있다. 로그에서도 바로 보인다.
 */

export type CategoryBand = 1 | 2 | 3;

/** 지금 새로 발급하는 유일한 대역 */
const PICKABLE_BAND: CategoryBand = 3;

const CATEGORY_ID_PATTERN = /^cat_([123])(\d{5})$/;

export interface ParsedCategoryId {
  band: CategoryBand;
  /** 대역 안의 일련번호. 0은 쓰지 않는다(cat_300000 없음). */
  sequence: number;
}

/** 형식과 대역이 모두 유효하면 파싱 결과를, 아니면 null을 준다. */
export function parseCategoryId(id: string): ParsedCategoryId | null {
  const matched = CATEGORY_ID_PATTERN.exec(id);
  if (matched === null) {
    return null;
  }
  const sequence = Number(matched[2]);
  // cat_x00000은 발급하지 않는다 — 일련번호는 1부터다.
  if (sequence === 0) {
    return null;
  }
  return { band: Number(matched[1]) as CategoryBand, sequence };
}

/**
 * Pick을 등록할 수 있는 id 형식인가.
 *
 * 형식만 본다 — 실제로 존재하는지, active인지는 카탈로그를 봐야 안다.
 * 여기서 걸러지는 것은 은퇴한 그룹 id(cat_1/cat_2)와 형식 오류다.
 */
export function isPickable(id: string): boolean {
  return parseCategoryId(id)?.band === PICKABLE_BAND;
}

/** 은퇴한 그룹 대역(cat_1 / cat_2)인가. 옛 참조를 알아볼 때 쓴다. */
export function isRetiredGroupId(id: string): boolean {
  const band = parseCategoryId(id)?.band;
  return band === 1 || band === 2;
}

/** 대역 + 일련번호로 id 문자열을 만든다. 빌드 스크립트가 새 id를 발급할 때 쓴다. */
export function formatCategoryId(band: CategoryBand, sequence: number): string {
  if (!Number.isInteger(sequence) || sequence < 1 || sequence > 99999) {
    throw new Error(`일련번호는 1~99999 정수여야 합니다: ${sequence}`);
  }
  return `cat_${band}${String(sequence).padStart(5, '0')}`;
}
